import { DailyEntry, VacationDay } from '../types';
import { TargetSummary, TargetException } from '../types/optimizedTargets';
import { parseISO } from 'date-fns'; // Removed unused addDays and differenceInCalendarDays

/**
 * Calculate target summary for a user's profile
 * This function efficiently calculates the core metrics for the target_summaries table
 */
export function calculateTargetSummary(
  startDate: string,
  endDate: string,
  workWeekDays: number[],
  yearlyTargetHours: number,
  vacationDays: VacationDay[],
  billedEntries: DailyEntry[],
  userId: string
): TargetSummary {
  // Parse dates
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const today = new Date();
  
  // Create a map of dates to hours billed for quick lookups
  const billedHoursByDate = new Map<string, number>();
  billedEntries.forEach(entry => {
    billedHoursByDate.set(entry.entry_date, entry.billed_hours);
  });
  
  // Create a set of vacation dates for quick lookups
  const vacationDateSet = new Set(
    vacationDays.map(vd => vd.vacation_date)
  );
  
  // Calculate total work days (excluding vacations) and hours billed so far
  let totalWorkDays = 0;
  let totalBilledHours = 0;
  let elapsedWorkDays = 0; // Renamed from workDaysBeforeToday for clarity
  let billedHoursBeforeToday = 0; // Keep this for potential future use, but not directly in summary
  
  // First pass: count days and calculate metrics
  let currentDate = new Date(start);
  while (currentDate <= end) {
    const dateString = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD
    const dayOfWeek = currentDate.getDay();
    const isVacationDay = vacationDateSet.has(dateString);
    const isWorkDay = workWeekDays.includes(dayOfWeek) && !isVacationDay;
    
    // Count work days
    if (isWorkDay) {
      totalWorkDays++;
      
      // Track elapsed work days (up to and including today)
      if (currentDate <= today) { 
        elapsedWorkDays++;
      }
    }
    
    // Add up billed hours
    if (billedHoursByDate.has(dateString)) {
      const hours = billedHoursByDate.get(dateString)!;
      totalBilledHours += hours;
      // Track hours billed up to today if needed separately
      if (currentDate <= today) {
         billedHoursBeforeToday += hours;
      }
    }
    
    // Go to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  // If there are no work days, set safe values to avoid division by zero
  if (totalWorkDays === 0) {
    totalWorkDays = 1; // Avoid division by zero, though this case is unlikely
  }
  
  // Calculate average daily target based on total available work days
  const avgDailyTarget = totalWorkDays > 0 ? yearlyTargetHours / totalWorkDays : 0;
  const remainingWorkDays = totalWorkDays - elapsedWorkDays;
  const remainingHours = yearlyTargetHours - totalBilledHours;

  // --- Calculate Projected Completion Date --- 
  let projectedCompletionDate: string | null = null;
  if (avgDailyTarget > 0 && remainingHours > 0) {
    // Estimate remaining work days needed based on average target
    const estimatedRemainingWorkDaysNeeded = Math.ceil(remainingHours / avgDailyTarget);
    
    // Find the date that corresponds to completing these remaining work days
    let futureDate = new Date(today); // Start from today
    let workDaysCounted = 0;
    let safetyBreak = 0; // Prevent infinite loops
    const maxFutureDays = 365 * 5; // Look ahead max 5 years

    while (workDaysCounted < estimatedRemainingWorkDaysNeeded && safetyBreak < maxFutureDays) {
      futureDate.setDate(futureDate.getDate() + 1); // Move to the next day
      const futureDateString = futureDate.toISOString().split('T')[0];
      const futureDayOfWeek = futureDate.getDay();
      const isFutureVacation = vacationDateSet.has(futureDateString);
      const isFutureWorkDay = workWeekDays.includes(futureDayOfWeek) && !isFutureVacation;

      if (isFutureWorkDay) {
        workDaysCounted++;
      }
      safetyBreak++;
    }

    // If we found the date within the safety limit
    if (safetyBreak < maxFutureDays) {
      projectedCompletionDate = futureDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    }
  } else if (remainingHours <= 0) {
    // If target is already met, find the last day hours were billed or today
    let lastBilledDate = today;
    billedEntries.forEach(entry => {
        const entryDate = parseISO(entry.entry_date);
        if (entryDate > lastBilledDate) {
            lastBilledDate = entryDate;
        }
    });
    projectedCompletionDate = lastBilledDate.toISOString().split('T')[0];
  }
  // --- End Projected Completion Date Calculation ---
  
  // Create the target summary object
  const targetSummary: TargetSummary = {
    profile_id: userId,
    yearly_target_hours: yearlyTargetHours,
    avg_daily_target: avgDailyTarget,
    remaining_hours: remainingHours,
    remaining_work_days: remainingWorkDays,
    elapsed_work_days: elapsedWorkDays, // Use the new name
    total_work_days: totalWorkDays,
    total_billed_hours: totalBilledHours,
    projected_completion_date: projectedCompletionDate, // Add the calculated date
    last_calculated_at: new Date().toISOString()
  };
  
  return targetSummary;
}

/**
 * Generate target exceptions from vacation days
 * This creates exception records for vacation days with is_work_day=false
 */
export function generateTargetExceptions(
  userId: string,
  vacationDays: VacationDay[]
): Omit<TargetException, 'id'>[] {
  return vacationDays.map(vacation => ({
    profile_id: userId,
    exception_date: vacation.vacation_date,
    target_hours: 0,
    is_work_day: false,
    is_vacation_day: true,
    reason: 'vacation' as const
  }));
}

/**
 * Generate target exceptions for non-standard work days
 * This includes holidays and any custom exceptions
 */
export function generateCustomExceptions(
  userId: string, 
  exceptionDates: Array<{date: string, reason: string, hours?: number}>
): Omit<TargetException, 'id'>[] {
  return exceptionDates.map(exception => ({
    profile_id: userId,
    exception_date: exception.date,
    target_hours: exception.hours || 0,
    is_work_day: exception.hours !== undefined && exception.hours > 0,
    is_vacation_day: exception.reason === 'vacation',
    reason: (exception.reason === 'vacation' || exception.reason === 'holiday') 
      ? exception.reason 
      : 'custom' as const
  }));
}
